// A tiny ring buffer of "what happened recently on this device" — the
// context that turns a bare exception or freeze report into something
// diagnosable. The AR launch firefight's lesson: the failure itself is often
// the LAST event of a sequence (route → AR start → degrade → freeze), and
// without the sequence the report is a dead end.
//
// Kept in sessionStorage so a crumb trail survives the reload AFTER a crash
// or freeze (that's exactly when the next boot wants to attach it to a
// report), while staying per-tab and dying with the session — consistent
// with the cookieless analytics model: no persistent identifier, no
// cross-session linkage.
import {
  CAUGHT_ERROR_EVENT,
  type CaughtErrorDetail,
} from 'magical-trails-react';

export interface Breadcrumb {
  /** Epoch ms. */
  t: number;
  kind: string;
  detail?: string;
}

const KEY = 'mt-breadcrumbs';
const MAX_CRUMBS = 20;
const MAX_DETAIL_CHARS = 160;

function read(): Breadcrumb[] {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    // Storage is untrusted: a malformed entry (corrupt write, another tab on
    // an old build) must be dropped, not crash the formatter — an invalid
    // `t` would make toISOString throw inside getBreadcrumbTrail.
    return parsed.filter(
      (c): c is Breadcrumb =>
        !!c &&
        typeof c === 'object' &&
        typeof (c as Breadcrumb).t === 'number' &&
        Number.isFinite((c as Breadcrumb).t) &&
        typeof (c as Breadcrumb).kind === 'string' &&
        ((c as Breadcrumb).detail === undefined ||
          typeof (c as Breadcrumb).detail === 'string')
    );
  } catch {
    return [];
  }
}

export function addBreadcrumb(kind: string, detail?: string): void {
  try {
    const crumbs = read();
    crumbs.push({
      t: Date.now(),
      kind,
      detail: detail?.slice(0, MAX_DETAIL_CHARS),
    });
    window.sessionStorage.setItem(
      KEY,
      JSON.stringify(crumbs.slice(-MAX_CRUMBS))
    );
  } catch {
    // Diagnostics must never break the app.
  }
}

/** Compact, human-scannable form for attaching to PostHog events. */
export function getBreadcrumbTrail(): string[] {
  return read().map(
    (c) =>
      `${new Date(c.t).toISOString().slice(11, 19)} ${c.kind}${
        c.detail ? `: ${c.detail}` : ''
      }`
  );
}

/**
 * Install the passive collectors: SPA route changes (history patching — the
 * router offers no global subscription from here) and the shared library's
 * caught-error events. Other modules add their own crumbs explicitly via
 * addBreadcrumb. Idempotence guard included since index.tsx is the only
 * intended caller but hot reload re-executes modules in dev.
 */
let installed = false;
export function installBreadcrumbCollectors(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  addBreadcrumb('boot', window.location.pathname);

  const recordNavigation = () => {
    addBreadcrumb('route', window.location.pathname);
  };
  const wrapHistory = (method: 'pushState' | 'replaceState') => {
    const original = window.history[method].bind(window.history);
    window.history[method] = (...args: Parameters<History['pushState']>) => {
      original(...args);
      recordNavigation();
    };
  };
  wrapHistory('pushState');
  wrapHistory('replaceState');
  window.addEventListener('popstate', recordNavigation);

  window.addEventListener(CAUGHT_ERROR_EVENT, (event) => {
    const detail = (event as CustomEvent<CaughtErrorDetail>).detail;
    if (!detail) return;
    addBreadcrumb(
      `caught:${detail.kind}`,
      detail.message ??
        (detail.error instanceof Error ? detail.error.message : undefined)
    );
  });
}
